import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useAppDispatch } from '../../redux/hooks';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import TabsContainer, { TabItem } from '../../shared-components/tabs-container/TabsContainer';
import DiagnosticFirmware from '../diagnostic-firmware';
import Firmware from '../firmware';

import OcppLogsTable, { OCPPLogsProperties } from './components/OcppLogsTable';
import './scss/index.scss';

/**
 * OCPP Logs and Diagnostics & Firmware tabbed page
 * @param {OCPPLogsProperties} properties - Component properties
 * @returns {React.ReactElement} The rendered OCPPLogs page
 */
function OCPPLogs(properties: OCPPLogsProperties = {}): React.ReactElement {
  const {
    account,
    chargingStation,
    chargingPointSn,
    autoRefreshEnabled = false,
    hideAccountStationChargerFilters = false,
    hideTabs = false,
  } = properties;
  const { t } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();

  const [tabs, setTabs] = useState<TabItem[]>([
    {
      label: t('oCPPLogs', 'OCPP Logs'),
      key: 'ocpp-logs',
      isActive: true,
    },
    ...(!pathname.includes('ocpp-logs')
      ? [
          {
            label: t('diagnostics', 'Diagnostics'),
            key: 'diagnostics',
            isActive: false,
          },
          {
            label: t('firmware', 'Firmware'),
            key: 'firmware',
            isActive: false,
          },
        ]
      : []),
  ]);

  useEffect(() => {
    setTabs((previousTabs) =>
      previousTabs.map((tab) => {
        switch (tab.key) {
          case 'ocpp-logs':
            return { ...tab, label: t('oCPPLogs', 'OCPP Logs') };
          case 'diagnostics':
            return { ...tab, label: t('diagnostics', 'Diagnostics') };
          case 'firmware':
            return { ...tab, label: t('firmware', 'Firmware') };
          default:
            return tab;
        }
      }),
    );
  }, [t]);

  /**
   * Function to switch the active tab
   * @param {string} key - The tab key to activate
   */
  const handleSwitchTab = (key: string) => {
    setTabs((previousTabs) =>
      previousTabs.map((tab) => ({
        ...tab,
        isActive: tab.key === key,
      })),
    );
    if (key === 'diagnostics') {
      dispatch(setPageTitle(t('diagnostics', 'Diagnostics')));
    } else if (key === 'firmware') {
      dispatch(setPageTitle(t('firmware', 'Firmware')));
    }
  };

  if (hideTabs) {
    return (
      <OcppLogsTable
        account={account}
        chargingStation={chargingStation}
        chargingPointSn={chargingPointSn}
        autoRefreshEnabled={autoRefreshEnabled}
        hideAccountStationChargerFilters={hideAccountStationChargerFilters}
      />
    );
  }

  const activeTab = tabs.find((tab) => tab.isActive);

  return (
    <div className="ocpp-logs-tabs-container-wrap">
      <TabsContainer
        containerTabs={tabs.filter((tab) => tab.allowed !== false)}
        handleSwitchTab={handleSwitchTab}
      />
      <div className="ocpp-logs-tabs-content">
        {(() => {
          switch (activeTab?.key) {
            case 'ocpp-logs':
              return (
                <OcppLogsTable
                  account={account}
                  chargingStation={chargingStation}
                  chargingPointSn={chargingPointSn}
                  autoRefreshEnabled={autoRefreshEnabled}
                  hideAccountStationChargerFilters={hideAccountStationChargerFilters}
                />
              );
            case 'diagnostics':
              return <DiagnosticFirmware chargingPointSn={chargingPointSn} />;
            case 'firmware':
              return <Firmware chargingPointSn={chargingPointSn} />;
            default:
              return null;
          }
        })()}
      </div>
    </div>
  );
}

OCPPLogs.defaultProps = {
  account: '',
  chargingStation: '',
  chargingPointSn: '',
  autoRefreshEnabled: false,
  disableAutoRefreshToggle: false,
  hideAccountStationChargerFilters: false,
  hideTabs: false,
};

export { OcppLogsTable };
export type { OCPPLogsProperties };
export default OCPPLogs;
